import { RankKit } from "@/components/site/RankKit";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand, FAQ, SectionHeader } from "@/components/site/Blocks";
import { CalcCard, CalcField, CalcRow, CalcStat, usd, pct } from "@/components/site/CalcKit";
import { ToolLeadGate } from "@/components/site/ToolLeadGate";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { ShareResultsBar } from "@/components/site/ShareResultsBar";
import { CalculatorAiPanel } from "@/components/site/CalculatorAiPanel";
import { CalculatorStateGate } from "@/components/site/CalculatorStateGate";
import { trackCalcCta, trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import capRateHero from "@/assets/hero/hero-cap-rate-multifamily.jpg";

const FAQS = [
  {
    q: "What is cap rate in real estate?",
    a: "Capitalization rate is net operating income divided by property value or purchase price. NOI excludes financing and income taxes, so cap rate measures the unlevered yield of the asset itself — it lets you compare two properties regardless of how each one is financed.",
  },
  {
    q: "What is a good cap rate for a rental property?",
    a: "It is market-specific. Stabilized single-family and small multifamily rentals in the Southeast and Midwest markets we lend in commonly trade in the 5%–8% band, while higher-cost coastal metros trade lower because buyers are paying for appreciation and liquidity. A cap rate is only meaningful against comparable sales in the same submarket and asset class.",
  },
  {
    q: "Does cap rate include the mortgage payment?",
    a: "No. Cap rate deliberately excludes debt service. If you want a levered return figure, use cash-on-cash return, which divides annual pre-tax cash flow after the loan payment by the cash you invested.",
  },
  {
    q: "How do I calculate NOI?",
    a: "NOI = gross scheduled rent and other income, minus vacancy loss, minus operating expenses (property taxes, insurance, management, maintenance and reserves, HOA, utilities you pay, and turnover). Mortgage principal and interest, depreciation, and capital improvements are excluded from NOI.",
  },
  {
    q: "What is reverse cap rate valuation?",
    a: "If you know the NOI and the market cap rate, value = NOI ÷ cap rate. Investors use this to price small multifamily and to test whether a listed price is supported by income. This calculator shows the implied value at your target cap rate next to the price you entered.",
  },
  {
    q: "How does cap rate relate to property value trends?",
    a: "Cap rates and prices move inversely when NOI is held constant — as buyers accept lower cap rates, they are paying more per dollar of income, and as cap rates rise, values compress. Watching cap rate trends in a submarket over several quarters can signal whether pricing is expanding or contracting relative to rents.",
  },
  {
    q: "What is a cap rate spread and why does it matter to investors?",
    a: "The cap rate spread is the gap between the property's cap rate and current borrowing costs; a wider positive spread generally means leverage adds to your cash-on-cash return, while a negative spread means debt drags on it. Investors evaluating a DSCR purchase often check this spread before deciding how much leverage to use.",
  },
  {
    q: "Does deferred maintenance affect cap rate?",
    a: "Deferred maintenance does not change the formula, but buyers typically underwrite a higher cap rate on properties needing near-term capital work to compensate for the added cost and risk, which effectively lowers the price they will pay for the same NOI. Sellers who address major deferred items before listing often support a lower, more favorable cap rate.",
  },
  {
    q: "How do I use cap rate to compare markets?",
    a: "Compare cap rates only within similar asset classes and property conditions, since a 6% cap rate in one metro may reflect different risk and growth assumptions than a 6% cap rate elsewhere. Pair the cap rate with rent growth trends, vacancy rates, and property tax trajectories in each market for a fuller comparison.",
  },
  {
    q: "What is the relationship between cap rate and DSCR?",
    a: "Cap rate measures unlevered yield on the purchase price, while DSCR measures whether in-place or projected rent covers the proposed loan payment. A property can have an attractive cap rate but still fail a DSCR loan's minimum ratio if the purchase price and financing terms push the payment too high relative to rent.",
  },
  {
    q: "Should I use in-place rent or market rent when calculating cap rate on a purchase?",
    a: "Use in-place, actual trailing rent and expenses for the most defensible cap rate at acquisition, and calculate a separate pro-forma cap rate using market rent as a forward-looking scenario. Lenders and appraisers generally weight actual trailing income more heavily than optimistic pro-forma projections.",
  },
  {
    q: "Why do cap rates differ between single-family rentals and small multifamily?",
    a: "Small multifamily properties often trade at higher cap rates than comparable single-family rentals because they carry more operating complexity, higher turnover exposure, and a smaller buyer pool of qualified investors. This calculator's inputs work for either property type as long as your income and expense figures reflect the specific asset.",
  },
];

export const Route = createFileRoute("/cap-rate-calculator")({
  head: () =>
    buildHead({
      title: "Cap Rate Calculator — NOI, Yield & Value | Simply Approved",
      description:
        "Free cap rate calculator for rental and multifamily property. Calculate NOI, cap rate, implied value at a target cap, GRM, and expense ratio in one place.",
      url: "/cap-rate-calculator",
      keywords:
        "cap rate calculator, capitalization rate calculator, NOI calculator, net operating income calculator, what is a good cap rate, cap rate formula, reverse cap rate valuation, multifamily cap rate calculator, rental property cap rate, cap rate vs cash on cash, investment property valuation calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Cap Rate Calculator", url: "/cap-rate-calculator" },
      ],
      service: { name: "Cap Rate Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Cap Rate Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/cap-rate-calculator",
          description: "Free cap rate and NOI calculator for rental and multifamily investment property.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: CapRatePage,
});

type Inputs = {
  price: number;
  grossRentMo: number;
  otherIncomeMo: number;
  vacancyPct: number;
  taxesAnnual: number;
  insAnnual: number;
  mgmtPct: number;
  maintenancePct: number;
  reservesPct: number;
  hoaMonthly: number;
  utilitiesMonthly: number;
  otherOpexAnnual: number;
  targetCapPct: number;
};

const DEFAULT: Inputs = {
  price: 425000,
  grossRentMo: 3600,
  otherIncomeMo: 75,
  vacancyPct: 5,
  taxesAnnual: 4800,
  insAnnual: 2100,
  mgmtPct: 8,
  maintenancePct: 5,
  reservesPct: 5,
  hoaMonthly: 0,
  utilitiesMonthly: 0,
  otherOpexAnnual: 0,
  targetCapPct: 7,
};

function CapRatePage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("cap_rate", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("cap_rate", String(k), v as never);
  };

  const m = useMemo(() => {
    const gsiMo = i.grossRentMo + i.otherIncomeMo;
    const vacancyMo = gsiMo * (i.vacancyPct / 100);
    const egiMo = gsiMo - vacancyMo;
    const mgmtMo = egiMo * (i.mgmtPct / 100);
    const maintMo = gsiMo * (i.maintenancePct / 100);
    const reservesMo = gsiMo * (i.reservesPct / 100);
    const fixedMo = i.taxesAnnual / 12 + i.insAnnual / 12 + i.hoaMonthly + i.utilitiesMonthly + i.otherOpexAnnual / 12;
    const opexMo = mgmtMo + maintMo + reservesMo + fixedMo;
    const noiMo = egiMo - opexMo;
    const noiYr = noiMo * 12;
    const capRate = i.price > 0 ? noiYr / i.price : 0;
    const impliedValue = i.targetCapPct > 0 ? noiYr / (i.targetCapPct / 100) : 0;
    const valueGap = impliedValue - i.price;
    const grm = gsiMo > 0 ? i.price / (gsiMo * 12) : 0;
    const expenseRatio = egiMo > 0 ? opexMo / egiMo : 0;
    return { gsiMo, vacancyMo, egiMo, opexMo, noiMo, noiYr, capRate, impliedValue, valueGap, grm, expenseRatio, mgmtMo, maintMo, reservesMo, fixedMo };
  }, [i]);

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackCalcEstimateViewed("cap_rate", { cap_rate: Number((m.capRate * 100).toFixed(2)), noi_annual: Math.round(m.noiYr) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Purchase price / value", value: usd(i.price) },
    { label: "Gross monthly rent", value: usd(i.grossRentMo) },
    { label: "Annual NOI", value: usd(m.noiYr) },
    { label: "Cap rate", value: pct(m.capRate) },
    { label: "Target cap rate", value: `${i.targetCapPct}%` },
    { label: "Implied value at target cap", value: usd(m.impliedValue) },
    { label: "Operating expense ratio", value: pct(m.expenseRatio, 1) },
    { label: "Gross rent multiplier", value: m.grm.toFixed(1) },
  ];


  const capColor = m.capRate >= 0.07 ? "text-emerald-600" : m.capRate >= 0.05 ? "text-amber-600" : "text-destructive";

  return (
    <SiteShell>
      <PageHero
        eyebrow="Cap rate calculator"
        title="Cap Rate Calculator: NOI, Yield and Implied Value"
        subtitle="Build NOI line by line, see the cap rate the price implies, and compare it to the value your target cap rate supports. Figures are illustrative estimates, not an appraisal or an offer of credit."
        primaryHref="/apply?product=dscr-loans"
        primaryLabel="Get Your Personalized Rate Quote"
        secondaryHref="/rates"
        secondaryLabel="See Today's Rates"
        image={{ src: capRateHero, alt: "Well-maintained duplex rental investment property at sunset" }}
      />
      <RankKit page="cap-rate-calculator" part="answer" />
      <PageEssentials hideAnswer />

      <Section>
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <CalcCard title="1. Price & income">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Purchase price / value" value={i.price} onChange={(v) => set("price", v)} prefix="$" />
                <CalcField label="Gross monthly rent" value={i.grossRentMo} onChange={(v) => set("grossRentMo", v)} prefix="$" hint="All units combined." />
                <CalcField label="Other monthly income" value={i.otherIncomeMo} onChange={(v) => set("otherIncomeMo", v)} prefix="$" />
                <CalcField label="Vacancy & credit loss" value={i.vacancyPct} onChange={(v) => set("vacancyPct", v)} suffix="%" step={1} />
              </div>
            </CalcCard>

            <CalcCard title="2. Operating expenses">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Property taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" hint="Use the reassessed value, not the seller's bill." />
                <CalcField label="Insurance (annual)" value={i.insAnnual} onChange={(v) => set("insAnnual", v)} prefix="$" />
                <CalcField label="Management" value={i.mgmtPct} onChange={(v) => set("mgmtPct", v)} suffix="%" step={1} />
                <CalcField label="Maintenance" value={i.maintenancePct} onChange={(v) => set("maintenancePct", v)} suffix="%" step={1} />
                <CalcField label="Capex reserves" value={i.reservesPct} onChange={(v) => set("reservesPct", v)} suffix="%" step={1} />
                <CalcField label="HOA (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
                <CalcField label="Owner-paid utilities (monthly)" value={i.utilitiesMonthly} onChange={(v) => set("utilitiesMonthly", v)} prefix="$" />
                <CalcField label="Other operating costs (annual)" value={i.otherOpexAnnual} onChange={(v) => set("otherOpexAnnual", v)} prefix="$" />
              </div>
            </CalcCard>

            <CalcCard title="3. Target cap rate">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Your target / market cap rate" value={i.targetCapPct} onChange={(v) => set("targetCapPct", v)} suffix="%" step={0.25} hint="Pull this from recent comparable sales in the same submarket." />
              </div>
            </CalcCard>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <CalculatorStateGate calculator="cap_rate">
            <ToolLeadGate
              calculator="cap_rate"
              storageKey="cap-rate-calc:lead-unlocked"
              product="Cap Rate Calculator"
              sourcePage="Cap Rate Calculator"
              title="Unlock your NOI and valuation analysis"
              subtitle="See NOI, cap rate, implied value at your target cap, expense ratio and GRM — and we'll email you a copy."
              getContext={() => ({
                price: i.price,
                gross_rent_mo: i.grossRentMo,
                noi_annual: Math.round(m.noiYr),
                cap_rate: (m.capRate * 100).toFixed(2) + "%",
                target_cap: i.targetCapPct + "%",
                implied_value: Math.round(m.impliedValue),
              })}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <CalcStat label="Cap rate" value={pct(m.capRate)} colorClass={capColor} />
                  <CalcStat label="Annual NOI" value={usd(m.noiYr)} />
                  <CalcStat label={`Value at ${i.targetCapPct}% cap`} value={usd(m.impliedValue)} />
                  <CalcStat
                    label="Vs. your price"
                    value={`${m.valueGap >= 0 ? "+" : "−"}${usd(Math.abs(m.valueGap))}`}
                    colorClass={m.valueGap >= 0 ? "text-emerald-600" : "text-destructive"}
                  />
                </div>

                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h3 className="font-display text-base font-semibold text-foreground">NOI build-up (monthly)</h3>
                  <dl className="mt-3 space-y-1.5 text-sm">
                    <CalcRow label="Gross scheduled income" value={usd(m.gsiMo)} />
                    <CalcRow label="Less vacancy & credit loss" value={`−${usd(m.vacancyMo)}`} />
                    <CalcRow label="Effective gross income" value={usd(m.egiMo)} strong />
                    <CalcRow label="Management" value={`−${usd(m.mgmtMo)}`} />
                    <CalcRow label="Maintenance" value={`−${usd(m.maintMo)}`} />
                    <CalcRow label="Capex reserves" value={`−${usd(m.reservesMo)}`} />
                    <CalcRow label="Taxes, insurance, HOA, utilities, other" value={`−${usd(m.fixedMo)}`} />
                    <CalcRow label="Total operating expenses" value={`−${usd(m.opexMo)}`} strong />
                    <CalcRow label="Monthly NOI" value={usd(m.noiMo)} strong colorClass={m.noiMo >= 0 ? "text-emerald-600" : "text-destructive"} />
                    <CalcRow label="Operating expense ratio" value={pct(m.expenseRatio, 1)} />
                    <CalcRow label="Gross rent multiplier" value={m.grm.toFixed(1)} />
                  </dl>
                </div>

                <Link
                  onClick={() => trackCalcCta("cap_rate", "related_tool")}
                  to="/cash-on-cash-return-calculator"
                  className="block rounded-md bg-brand px-4 py-3 text-center text-sm font-semibold text-primary-foreground hover:bg-brand/90"
                >
                  Add financing: cash-on-cash return →
                </Link>
                <Link
                  onClick={() => trackCalcCta("cap_rate", "apply")}
                  to="/apply"
                  className="block rounded-md border border-brand-gold bg-brand-gold px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-brand-gold-hover"
                >
                  Get Your Personalized Rate Quote
                </Link>
              </div>
            </ToolLeadGate>
            <ShareResultsBar
              calculator="cap_rate"
              shareUrl={shareUrl}
              subject="My cap rate scenario"
              summaryTitle="Cap rate scenario — key numbers"
              keyNumbers={aiFacts.slice(0, 6)}
            />
            <CalculatorAiPanel
              calculator="cap_rate"
              facts={aiFacts}
              suggestions={[
                  "What does this cap rate tell me about the price?",
                  "How is NOI different from cash flow?",
                  "Why does the implied value differ from my purchase price?",
                  "What expenses do investors most often understate?",
                ]}
            />
            </CalculatorStateGate>
          </aside>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl space-y-4 text-sm text-foreground/90">
          <SectionHeader
            title="How cap rate actually gets used"
            intro="Cap rate is a pricing language, not a return target. Here is how investors and lenders read it."
          />
          <p>
            <strong>Pricing.</strong> Small multifamily and commercial assets are quoted on cap rate because it strips out
            financing. If comparable stabilized duplexes in the submarket trade at a 7% cap and the NOI is $29,750, the income
            supports roughly $425,000 — regardless of what any single buyer&apos;s loan looks like.
          </p>
          <p>
            <strong>Value-add.</strong> The gap between the in-place cap rate and the stabilized cap rate is the business plan.
            Raise NOI by $6,000 a year in a 7% cap market and you have created roughly $85,000 of value, which is exactly the
            math behind a{" "}
            <Link to="/brrrr-calculator" className="text-brand underline">BRRRR refinance</Link>.
          </p>
          <p>
            <strong>What breaks it.</strong> Understated taxes at the reassessed value, an insurance quote from before the last
            hard-market cycle, no capex reserve, and self-management priced at zero. Strip those four shortcuts out and most
            &quot;9% cap&quot; listings underwrite closer to 6%.
          </p>
          <p>
            <strong>Where DSCR fits.</strong> Cap rate values the asset; DSCR decides the loan. A property can price well on cap
            rate and still miss a lender&apos;s minimum DSCR once the payment is included — run{" "}
            <Link to="/dscr-calculator" className="text-brand underline">the DSCR calculator</Link> before you write the offer.
          </p>
        </div>
      </Section>

      <RankKit page="cap-rate-calculator" part="details" />
      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow="Live pricing · DSCR"
        heading="Current DSCR pricing for income-qualified rentals"
        paragraphs={[
          "Pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property, including rate, points or lender credit, and estimated principal and interest from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/multifamily-loans", label: "5+ unit multifamily financing" },
        ]}
      />

      <Section>
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="Cap rate FAQ" intro="Straight answers on NOI, yield, and valuation." />
          <div className="mt-6">
            <FAQ items={FAQS} />
          </div>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Related investor tools</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/cash-on-cash-return-calculator" as const, label: "Cash-on-cash return calculator" },
              { to: "/arv-calculator" as const, label: "ARV calculator and the 70% rule" },
              { to: "/dscr-calculator" as const, label: "DSCR calculator" },
              { to: "/brrrr-calculator" as const, label: "BRRRR calculator" },
              { to: "/best-rental-markets" as const, label: "Best rental markets for cash flow" },
              { to: "/tools/compare-cities" as const, label: "Compare investor cities side by side" },
            ].map((l) => (
              <li key={l.to}><Link to={l.to} className="text-brand underline">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      </Section>

      <CTABand
        title="Financing for income-qualified property"
        body="Send us the NOI and we'll return an illustrative DSCR structure for the asset — business-purpose, non-owner-occupied investment property only."
      />
    <PageOutro />
      </SiteShell>
  );
}
